import React from 'react';
import { useFlowSettingsStore } from '../../store/flowSettingsStore';
import { fmtAge } from './StatusTab';
import {
  EDGE_HORIZONS,
  EDGE_TARGETS,
  EDGE_T_RELIABLE,
  edgeEntries,
  edgeLabel,
  fmtPct,
  fmtSigned,
  tuneDays,
  type FlowEdgeStats,
} from './flowSettings';

const HORIZON_LABEL: Record<string, string> = { '60': '1m', '120': '2m', '180': '3m', '240': '4m', '300': '5m' };

/** Mean ticks across the 1-5 min horizons as a tiny line around zero. */
export const EdgeSparkline: React.FC<{ means: (number | null)[]; reading: string | null }> = ({ means, reading }) => {
  const W = 90;
  const H = 26;
  const vals = means.map((m) => (m === null || !Number.isFinite(m) ? null : m));
  const max = Math.max(1, ...vals.map((v) => Math.abs(v ?? 0)));
  const x = (i: number) => (vals.length <= 1 ? W / 2 : 3 + (i * (W - 6)) / (vals.length - 1));
  const y = (v: number) => H / 2 - (v / max) * (H / 2 - 3);
  const pts = vals.map((v, i) => (v === null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`)).filter(Boolean);
  const cls = reading === 'continuation' ? 'is-up' : reading === 'reversal' ? 'is-down' : '';
  return (
    <svg
      className={`tf-edge-spark ${cls}`}
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`mean ticks 1-5 min: ${vals.map((v) => fmtSigned(v)).join(', ')}`}
    >
      <line x1="0" x2={W} y1={H / 2} y2={H / 2} className="tf-edge-spark-zero" />
      {pts.length > 1 && <polyline points={pts.join(' ')} fill="none" className="tf-edge-spark-line" />}
      {vals.map((v, i) => (v === null ? null : <circle key={i} cx={x(i)} cy={y(v)} r="1.8" className="tf-edge-spark-dot" />))}
    </svg>
  );
};

const tClass = (t: number | null | undefined) =>
  t !== null && t !== undefined && Math.abs(t) >= EDGE_T_RELIABLE ? 'is-strong' : 'is-weak';

/** One signal kind's edge, built for a 1-5 minute scalper. */
export const EdgeCard: React.FC<{ group: string; e: FlowEdgeStats }> = ({ group, e }) => {
  const hz = EDGE_HORIZONS.map((h) => e.horizons?.[h]);
  const ex = EDGE_HORIZONS.map((h) => e.excursions?.[h]);
  const reading = e.reading ?? null;
  const bandT = e.band_t ?? null;
  return (
    <div className="tf-edge-card" data-testid={`tradefinder-edge-${group}`}>
      <div className="tf-edge-head">
        <span className="tf-edge-name">{edgeLabel(group)}</span>
        <span className={`tf-edge-reading is-${reading ?? 'none'}`} data-testid={`tradefinder-edge-${group}-reading`}>
          {reading ?? 'no reading'}
        </span>
        <span className={`tf-edge-t ${tClass(bandT)}`} title="Mean t over the 1-5 min horizons; |t| below 2 = no reliable edge">
          band t {fmtSigned(bandT)}
        </span>
        <span className={`tf-edge-adopted ${e.adopted ? 'is-yes' : 'is-no'}`} title={e.reason}>
          {e.adopted ? 'adopted' : 'not adopted'}
        </span>
      </div>
      <div className="tf-edge-curve">
        <EdgeSparkline means={hz.map((h) => h?.mean ?? null)} reading={reading} />
        <table className="tf-edge-table">
          <thead>
            <tr>
              <th />
              {EDGE_HORIZONS.map((h) => (
                <th key={h} className={e.best_horizon_s === Number(h) ? 'is-best' : ''}>{HORIZON_LABEL[h]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr data-testid={`tradefinder-edge-${group}-mean`}>
              <th title="Mean forward move, ticks in the aggressor direction">ticks</th>
              {hz.map((h, i) => <td key={i}>{fmtSigned(h?.mean)}</td>)}
            </tr>
            <tr>
              <th>t</th>
              {hz.map((h, i) => <td key={i} className={tClass(h?.t)}>{fmtSigned(h?.t)}</td>)}
            </tr>
            <tr data-testid={`tradefinder-edge-${group}-mfe`}>
              <th title="Mean max favourable excursion within the horizon, ticks">MFE</th>
              {ex.map((x, i) => <td key={i}>{fmtSigned(x?.mfe).replace('+', '')}</td>)}
            </tr>
            <tr data-testid={`tradefinder-edge-${group}-mae`}>
              <th title="Mean max adverse excursion within the horizon, ticks">MAE</th>
              {ex.map((x, i) => <td key={i}>{fmtSigned(x?.mae).replace('+', '')}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="tf-edge-targets">
        {EDGE_TARGETS.map((t) => {
          const tg = e.targets?.[t];
          return (
            <span key={t} className="tf-edge-target" data-testid={`tradefinder-edge-${group}-target-${t}`}>
              +{t}/−{t}: <b>{fmtPct(tg?.win)}</b> win · {fmtPct(tg?.loss)} loss{tg ? ` (n ${tg.n})` : ''}
            </span>
          );
        })}
      </div>
      <div className="tf-edge-foot">
        <span>{e.n_per_day === null || e.n_per_day === undefined ? '—' : `${e.n_per_day}/day`}</span>
        <span title="Tuned threshold vs baseline">
          ×{e.multiplier ?? '—'} vs baseline
          {e.threshold ? ` (${e.threshold.baseline} → ${e.threshold.value})` : ''}
        </span>
        <span className="tf-edge-reason" data-testid={`tradefinder-edge-${group}-reason`}>{e.reason}</span>
      </div>
    </div>
  );
};

function fmtGenerated(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso || 'unknown';
  const local = new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  return `${local} (${fmtAge(now - t)} ago)`;
}

/** Self-tuner edge per signal kind for `root`, from GET /api/flow-tune. */
const FlowEdgeSection: React.FC<{ root: string }> = ({ root }) => {
  const tune = useFlowSettingsStore((s) => s.tune);
  const now = Date.now();

  let body: React.ReactNode;
  if (tune.status === 'idle' || tune.status === 'loading') {
    body = <div className="levels-empty">Loading tuner results…</div>;
  } else if (tune.status === 'none') {
    body = (
      <div className="tf-edge-empty" data-testid="tradefinder-edge-empty">
        The self-tuner hasn't run yet, or doesn't have enough recorded days. Edge stats show up here after the
        first nightly tune; your settings work without it.
      </div>
    );
  } else if (tune.status === 'error') {
    body = <div className="tf-edge-empty is-error" data-testid="tradefinder-edge-error">Tuner results unavailable: {tune.message}</div>;
  } else {
    const entries = edgeEntries(tune.data, root);
    const days = tuneDays(tune.data, root);
    body = (
      <>
        <div className="tf-edge-meta" data-testid="tradefinder-edge-meta">
          Last run {fmtGenerated(tune.data.generated, now)} · {days.count} day{days.count === 1 ? '' : 's'}
          {days.first && days.last ? ` (${days.first} – ${days.last})` : ''}
        </div>
        {entries.length === 0 ? (
          <div className="tf-edge-empty" data-testid="tradefinder-edge-empty-root">The last tune has no edge stats for {root}.</div>
        ) : (
          entries.map(([group, e]) => <EdgeCard key={group} group={group} e={e} />)
        )}
      </>
    );
  }

  return (
    <section className="tf-edge" data-testid="tradefinder-edge">
      <div className="levels-group-heading">Edge ({root}, self-tuner)</div>
      <div className="tf-edge-note">
        Ticks in the aggressor direction after the signal, recent days, at the tuned threshold. Positive = continuation.
      </div>
      {body}
    </section>
  );
};

export default FlowEdgeSection;
